import React from 'react';
import { CloudArrowUpIcon, CheckCircleIcon, ExclamationTriangleIcon, ClockIcon } from '@heroicons/react/24/outline';

const CompactAutoSaveIndicator = ({
  isAutoSaveEnabled,
  autoSaveStatus,
  lastSaveTime,
  nextSaveCountdown,
  toggleAutoSave,
  saveNow,
  hasManualSave = true, // Whether manual save has occurred (default true for backward compatibility)
  className = ""
}) => {
  const formatTime = (date) => {
    if (!date) return '';
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatCountdown = (seconds) => {
    if (seconds <= 0) return '';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const getStatusIcon = () => {
    switch (autoSaveStatus) {
      case 'saving':
        return <CloudArrowUpIcon className="h-4 w-4 animate-pulse text-white" />;
      case 'success':
        return <CheckCircleIcon className="h-4 w-4 text-green-300" />;
      case 'error':
        return <ExclamationTriangleIcon className="h-4 w-4 text-red-300" />;
      default:
        return isAutoSaveEnabled ? <ClockIcon className="h-4 w-4 text-white/80" /> : null;
    }
  };

  const getStatusText = () => {
    switch (autoSaveStatus) {
      case 'saving':
        return 'Saving...';
      case 'success':
        return 'Saved successfully';
      case 'error':
        return 'Save failed';
      default:
        if (!isAutoSaveEnabled) return 'Auto-save off';
        if (!hasManualSave) return 'Pending manual save';
        if (nextSaveCountdown > 0) return `Auto-save ${nextSaveCountdown}s`;
        return 'Auto-save ready';
    }
  };

  const getLastSavedText = () => {
    if (!lastSaveTime) return '';
    return `Last saved: ${formatTime(lastSaveTime)}`;
  };

  // Always show countdown when it's active and manual save has occurred
  const showCountdown = isAutoSaveEnabled && hasManualSave && nextSaveCountdown > 0 && autoSaveStatus === 'idle';

  return (
    <div className={`flex items-center space-x-3 ${className}`}>
      {/* Auto-save Toggle */}
      <button
        onClick={toggleAutoSave}
        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-white/20 ${
          isAutoSaveEnabled ? 'bg-green-400/60' : 'bg-white/10'
        }`}
        aria-label="Toggle auto-save"
        title={`Auto-save is ${isAutoSaveEnabled ? 'enabled (continuous)' : 'disabled'}`}
      >
        <span
          className={`inline-block h-3 w-3 transform rounded-full transition-transform ${
            isAutoSaveEnabled ? 'translate-x-5 bg-white' : 'translate-x-1 bg-white/70'
          }`}
        />
      </button>

      {/* Status with Icon and Progress */}
      <div className="flex items-center space-x-2">
        {/* Large Countdown Display - Show when counting down */}
        {showCountdown && (
          <div className="relative flex items-center space-x-1">
            <div className="relative w-7 h-7 flex items-center justify-center bg-green-400/40 rounded-full border-2 border-green-300/60 animate-pulse">
              <span className="text-sm text-white font-bold">
                {nextSaveCountdown}
              </span>
            </div>
          </div>
        )}
        
        {/* Status Icon - Show when not counting down */}
        {!showCountdown && getStatusIcon()}
        
        <div className="flex flex-col">
          <span className="text-sm text-white/90 font-medium min-w-[140px]">
            {getStatusText()}
          </span>
          {/* Always show last saved time when available */}
          {lastSaveTime && (
            <span className="text-xs text-green-200">
              {getLastSavedText()}
            </span>
          )}
          {/* Show continuous mode indicator only when no last save time */}
          {isAutoSaveEnabled && !showCountdown && autoSaveStatus === 'idle' && !lastSaveTime && (
            <span className="text-xs text-green-200">
              Continuous mode
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompactAutoSaveIndicator; 